import { useCurrentUserLocale } from "@app/shared/utils/currentUserLocale";
import { SupportedLocales } from "@app/shared/utils/locales";
import { Icon } from "@design-system/Icon";
import { Calendar } from "@design-system/Inputs/Calendar";
import { Textfield } from "@design-system/Inputs/Textfield";
import { FieldWrapper } from "@design-system/Inputs/Textfield/FieldWrapper";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { Box } from "@design-system/Layout/Box";
import { FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { RESOURCE_NAME, keys } from "./i18n";

type DateInputProps = {
    editable?: boolean;
    value?: Date;
    onChange: (value: Date | undefined) => void;
};

const MAX_END_DATE = new Date(new Date().getFullYear() + 50, 11, 31);

export const DateInput: FC<DateInputProps> = ({
    editable,
    value,
    onChange,
}) => {
    const { t } = useTranslation(RESOURCE_NAME);

    const handleChange = (date: Date | undefined) => {
        onChange(date);
    };

    const userLocale = useCurrentUserLocale(SupportedLocales.EN);

    return editable ? (
        <Dropdown>
            <Dropdown.Trigger className="w-[135px] text-left">
                <FieldWrapper
                    placeholder={t(keys.target_due_date)}
                    placeholderUp={!!value}
                    required
                >
                    <FlexRow gap="2" w="full" justifyContent="between">
                        <Text>{value?.toLocaleDateString() ?? ""}</Text>
                        <Icon
                            className="text-primary translate-y-[-9px]"
                            name="calendar"
                        />
                    </FlexRow>
                </FieldWrapper>
            </Dropdown.Trigger>
            <Dropdown.Content align="start" hasPortal>
                <Calendar
                    mode="single"
                    defaultMonth={value ? new Date(value) : new Date()}
                    selected={value}
                    onSelect={handleChange}
                    locale={{ code: userLocale }}
                    required={false}
                    className="rounded-md border"
                    endMonth={MAX_END_DATE}
                />
            </Dropdown.Content>
        </Dropdown>
    ) : (
        <Box className="w-[116px] pointer-events-none cursor-not-allowed">
            <Textfield
                required
                placeholder={t(keys.target_due_date)}
                value={value?.toLocaleDateString() ?? ""}
            />
        </Box>
    );
};
